import type { AutomationInputsType, NodeContextType } from '@unifyapps/defs/types/dataSource';
import _omit from 'lodash/omit';
import _pick from 'lodash/pick';
import { memo, useEffect } from 'react';
import isEqual from 'react-fast-compare';
import { KEYS_TO_MATCH } from '../../consts';
import { useDSCInterfaceRecord, useDSCIsPublicPage, useDSCMode } from '../../context/selectors';
import { useComputeDataSourcePayload } from '../../hooks/useComputeDataSourcePayload';
import { useExecuteDataSourceCallbacks } from '../../hooks/useExecuteDataSourceCallbacks';
import { useGenerateQueryOptionsFromAdvancedOptions } from '../../hooks/useGenerateQueryOptionsFromAdvancedOptions';
import type { DataSourceWorkflowNodeProps } from '../../types';
import { getEffectiveIsPublicPage } from '../../utils';
import { DataSourcePulseObserverGate } from '../DataSourcePulseObserver/DataSourcePulseObserverGate';
import { useAddSystemParameters } from './hooks/useAddSystemParameters';
import useStreamDataSourceWorkflowNode from './hooks/useStreamDataSourceWorkflowNode';

function StreamingDataSourceWorkflowNode({
  id,
  originalDataSourceId,
  requestId,
  inputs,
  context,
  lookupRequests,
  onQueryCompletion,
  name,
  callbacks,
  advancedOptions,
  setDataSourceState,
  getCurrentQueryResult,
  refetchOnPayloadChange,
  disabled,
  dependsOn,
  dynamicPaths,
  defaultRetryConfig,
}: DataSourceWorkflowNodeProps) {
  const mode = useDSCMode();
  const interfaceRecord = useDSCInterfaceRecord();
  const isPublicPage = useDSCIsPublicPage();
  const effectiveIsPublicPage = getEffectiveIsPublicPage(mode, isPublicPage);

  const computedPayload = useComputeDataSourcePayload<{
    inputs: AutomationInputsType;
    context?: NodeContextType;
  }>({
    data: { inputs, context },
    refetchOnPayloadChange,
    dataSourceId: id,
    key: requestId,
    dependsOn,
    dynamicPaths,
  });

  const decoratedInputs = useAddSystemParameters(computedPayload);
  const queryOptions = useGenerateQueryOptionsFromAdvancedOptions({
    advancedOptions,
    defaultRetryConfig,
  });

  const queryResult = useStreamDataSourceWorkflowNode({
    payload: {
      context: computedPayload.context,
      id: originalDataSourceId,
      inputs: decoratedInputs,
      lookupRequests,
    },
    enabled: !disabled,
    debugParams: {
      name,
      requestId,
      meta: {
        isPublicPage: effectiveIsPublicPage,
      },
    },
    queryOptions,
    interfaceRecord,
  });

  useEffect(() => {
    const currentQueryResult = getCurrentQueryResult({
      id,
      requestId,
    });

    if (isEqual(_pick(queryResult, KEYS_TO_MATCH), _pick(currentQueryResult, KEYS_TO_MATCH))) {
      return;
    }

    setDataSourceState({
      id,
      result: _omit(queryResult, 'outputSchema'),
      requestId,
    });
  }, [getCurrentQueryResult, queryResult, id, setDataSourceState, requestId]);

  useExecuteDataSourceCallbacks({
    callbacks,
    onQueryCompletion,
    requestId,
    id,
    outputSchema: queryResult.outputSchema,
    queryResult: {
      error: queryResult.error,
      isLoading: queryResult.isLoading,
      isFetching: queryResult.isFetching,
    },
    disabled,
  });

  return (
    <DataSourcePulseObserverGate
      dsId={id}
      enabled={!disabled}
      error={queryResult.error}
      isFetching={Boolean(queryResult.isFetching)}
      objects={queryResult.objects}
      requestPayload={decoratedInputs}
    />
  );
}

export default memo(StreamingDataSourceWorkflowNode);
